import React, {Fragment} from "react";
import {useSelector} from "react-redux";
import {
    ParameterComponent,
    PhotoMonitoringReport,
    ReportSections
} from "../Components/Pages/ReportBuilder";
import {service as ReportService} from "../Services/ReportService";
import {service as SampleEventPhaseService} from "../Services/SampleEventPhaseService";
import {useCachedServices} from "./ServiceHooks";
import {useDefaultErrorHandler} from "./ErrorHooks";
import {usePermission} from "./PermissionHooks";

const useGenerateReport = () => {
    const defaultErrorHandler = useDefaultErrorHandler();
    
    return async (params = {}) => {
        try {
            await ReportService.Print(params);
        } catch (error) {
            defaultErrorHandler(error);
        }
    };
}

export const useReportPages = () => {
    const programReportPages = useProgramReportPages();
    const projectReportPages = useProjectReportPages();
    const sampleEventPages = useSampleEventPages();
    const validationChecklistPages = useValidationChecklistPages();

    const {reportTypes, reportTypeId} = useSelector(state => state.reportState);

    const currentReportType = reportTypes.find(type => Number(type.reportTypeId) === Number(reportTypeId));

    switch (currentReportType?.code) {
        case 'program-annual':
            return programReportPages;
        case 'project-annual':
            return projectReportPages;
        case 'monitoring-event':
            return sampleEventPages;
        case 'project-validation-checklist':
            return validationChecklistPages;
        default:
            return [{component: <ParameterComponent />}];
    }
};

export const useProgramReportPages = () => {
    const {reportTypeId, programId, programYear} = useSelector(state => state.reportState);
    
    return [
        {
            component: (
                <Fragment>
                    <ParameterComponent />
                </Fragment>
            ),
            actions: [
                {
                    label: 'Generate Report (.docx)',
                    onPress: () => {
                        ReportService.Print({
                            reportTypeId,
                            programId,
                            programYear,
                        });
                    },
                    disabled: !programId,
                }
            ]
        },
    ];
};

export const useProjectReportPages = () => {
    const {reportTypeId, projectId, phases, phaseId, selectedSections} = useSelector(state => state.reportState);

    const currentPhase = phases?.find(phase => Number(phase.sampleEventPhaseId) === Number(phaseId));
    const procedureCodes = [...selectedSections['riparian-habitat'] ?? [], ...selectedSections['instream-habitat'] ?? []];

    return [
        {
            component: (
                <Fragment>
                    <ParameterComponent />
                    <ReportSections />
                </Fragment>
            ),
            actions: [
                {
                    label: 'Generate Report (.docx)',
                    onPress: () => {
                        ReportService.Print({
                            reportTypeId,
                            projectId,
                            sampleEventPhaseCode: currentPhase?.code,
                            procedureCodes
                        });
                    },
                    disabled: !projectId || !phaseId || (!procedureCodes.length),
                }
            ]
        },
    ];
};

export const useSampleEventPages = () => {
    const {projectId, project, phaseId, currentEvent, procedures, procedureId, monitoringEventId, photosLoaded } = useSelector(state => state.reportState);

    const currentProcedure = procedures?.find(procedure => Number(procedure.procedureId) === Number(procedureId));
    let procedureComponent;

    //const procedure = procedures?.find( p => p.procedureId === procedureId );
    //const currentEvent = currentProcedure?.SampleEvents?.find( se => String(se.sampleEventId) === String(monitoringEventId) );

    switch (currentProcedure?.code) {
        case 'photo':
            procedureComponent = <PhotoMonitoringReport  />;
            break;
        default:
            procedureComponent = null;
    }

    return [
        {
            component: <ParameterComponent />,
            nextButtonDisabled: !projectId || !procedureId || !monitoringEventId || !procedureComponent
        },
        {
            title: 'Photo Point Monitoring Report',
            component: procedureComponent,
            colSize: 12,
            actions: [
                {
                    label: photosLoaded ? 'Print' : 'Loading...',
                    onPress: () => {
                        document.title = `${project.name} - ${currentEvent?.shortName} - Photo Point Report`;
                        window.print();
                    },
                    disabled: !photosLoaded
                },
                {
                    label: 'Download (.zip)',
                    onPress: () => {
                        ReportService.DownloadPhotos(projectId, [currentEvent?.sampleEventId]);
                    },
                    disabled: !projectId || !currentEvent?.sampleEventId,
                }
            ]
        }
    ];
};

export const useValidationChecklistPages = () => {
    const {reportTypeId, projectId} = useSelector(state => state.reportState);
    
    const permission = usePermission();
    const generateReport = useGenerateReport();

    const reportParameters = {
        reportTypeId,
        projectId,
        sampleEventPhaseCode: 'pre-project',
    };

    return [
        {
            component: (
                <Fragment>
                    <ParameterComponent />
                </Fragment>
            ),
            actions: [
                {
                    label: 'Generate Report (.docx)',
                    onPress: () => void generateReport({
                        ...reportParameters,
                    }),
                    disabled: !projectId,
                },
                permission('can_update_survey') ?
                {
                    label: 'Preview Template Variables (.json)',
                    onPress: () => void generateReport({
                        ...reportParameters,
                        format: 'json',
                    }),
                    disabled: !projectId,
                } : null,
            ]
        },
    ];
};
